import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../components/Icon.jsx";
import { Shell } from "../components/Shell.jsx";
import { ACTIVATION_PATHS } from "../navConfig.js";

const PROCESS_STEPS = [
  {
    number: "01",
    title: "Start with eligibility",
    body: "Only members eligible for the selected benefit and measurement month enter that program's evaluation.",
  },
  {
    number: "02",
    title: "Normalize claims evidence",
    body: "Diagnosis, service, place-of-service, provider, timing, and authorization fields are translated into consistent program signals.",
  },
  {
    number: "03",
    title: "Apply program rules",
    body: "Evidence is grouped into an MSK episode, a diabetes registry or care-gap record, or a virtual-care encounter.",
  },
  {
    number: "04",
    title: "Resolve stage and action",
    body: "Precedence rules select the strongest current stage or safest route, then assign a recommended benefit action.",
  },
  {
    number: "05",
    title: "Apply guardrails",
    body: "Safety, clinical-review, inactive-history, and in-person-routing conditions can stop routine outreach.",
  },
  {
    number: "06",
    title: "Govern campaign use",
    body: "Quality, privacy, enrollment, message, cadence, reviewer, and channel checks run before a campaign can progress.",
  },
];

const PROGRAMS = [
  {
    name: "MSK",
    signal:
      "Body-region episodes, duration, conservative care, imaging, procedures, specialty, authorization, and surgery signals.",
    result: "Education, digital MSK/PT, navigation, pre-surgical review, or recovery support.",
    guardrail:
      "Recent trauma or fracture, cancer or infection, neurologic deficit, or surgery can require review.",
  },
  {
    name: "Diabetes",
    signal:
      "Diabetes and prediabetes diagnoses, device supplies, acute events, utilization, complications, and claims-based care gaps.",
    result: "Prevention, routine support, care-gap closure, digital management, or higher-touch review.",
    guardrail:
      "ESRD or dialysis, hospice, cancer or transplant, pregnancy, and other complex conditions require specialized routing.",
  },
  {
    name: "Virtual Care",
    signal:
      "Condition acuity, virtual-use history, asynchronous care, remote monitoring, setting, and hands-on service needs.",
    result: "Asynchronous, scheduled virtual, virtual-first, hybrid, in-person, or urgent routing.",
    guardrail:
      "Urgent and in-person-required signals always take precedence over a convenient virtual route.",
  },
];

const FAQS = [
  {
    question: "What does an opportunity mean?",
    answer:
      "It means the available claims and eligibility data matched a documented program rule for a potentially useful benefit action. It is not a diagnosis, a treatment recommendation, or proof that a member needs or wants the service.",
  },
  {
    question: "What makes a record an enrollment campaign candidate?",
    answer:
      "The member must be eligible, have a current program opportunity, pass the scoring and precedence rules, and have no guardrail or suppression reason that blocks routine outreach. Enrollment status then separates Goal 1 enrollment from Goal 2 program-use campaigns.",
  },
  {
    question: "Why can a suppressed opportunity still appear in reporting?",
    answer:
      "Suppression is a safety or routing control, not a statement that the original evidence disappeared. Suppressed records remain in aggregate reporting and, for authorized reviewers, the restricted review workflow, but they are excluded from routine campaign use.",
  },
  {
    question: "Does a high score or priority guarantee outreach?",
    answer:
      "No. A score helps order evidence within a program. Guardrails deliberately add review priority, so a high-priority record may be the least appropriate record for routine outreach. Suppression and governance controls always take precedence.",
  },
  {
    question: "How should claims-based care gaps be interpreted?",
    answer:
      "As evidence gaps that may deserve verification. A missing claim does not prove that care was missed because services outside the available claims, incomplete lab data, or late-arriving records may not be visible.",
  },
  {
    question: "How current and complete are the signals?",
    answer:
      "Each result is tied to an immutable evidence period and the program's documented lookback window. Claims are retrospective and can arrive late or omit clinical context, symptoms, lab values, pharmacy activity, or services paid outside the available feed.",
  },
  {
    question: "What can campaign messages say?",
    answer:
      "Messages may explain an available benefit and a safe next step. They must use an active approved Workday app template, include required content and safety language, and respect sensitivity rules. They must not present a claims signal as a diagnosis or expose claim-level reasoning.",
  },
  {
    question: "Who can approve and publish a campaign?",
    answer:
      "Campaigns follow maker-checker controls: the creator cannot approve their own version. Governance is rerun at approval and publication, and only users with the appropriate reviewer or operator role can perform those transitions.",
  },
  {
    question: "How is member privacy protected?",
    answer:
      "Normal HR screens return aggregate counts only, and small nonzero groups are suppressed. Exact member IDs used for contact-conflict checks are converted to one-way keyed hashes. Restricted member detail requires a dedicated role, tenant-scoped queries, no export, and an access audit.",
  },
  {
    question: "Does publishing a campaign send messages today?",
    answer:
      "No. Publishing freezes the approved definition, message schedule, and recipient decisions for the restricted delivery-service handoff. An external sender, confirmed delivery/read feedback, and outcome attribution are not wired into this application.",
  },
];

function TrustCard({ icon, title, children }) {
  return (
    <div className="method-trust-card">
      <span className="method-icon">
        <Icon name={icon} size={20} strokeWidth={1.9} />
      </span>
      <div>
        <h3>{title}</h3>
        <p>{children}</p>
      </div>
    </div>
  );
}

const SECTION_LINKS = [
  ["identification", "Identification process"],
  ["programs", "Program methods"],
  ["guardrails", "Guardrails"],
  ["messages", "Message limits"],
  ["faq", "FAQ"],
];

export function Methodology({ tenant, auth, params }) {
  const requestedSection = params?.get("section");

  useEffect(() => {
    if (!requestedSection) return;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(requestedSection)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [requestedSection]);

  return (
    <Shell
      tenant={tenant}
      auth={auth}
      activeNav="methodology"
      title="Methodology & FAQ"
      subtitle="How opportunities are identified, protected, and prepared for responsible campaign use"
    >
      <section className="method-hero" aria-labelledby="method-overview-title">
        <div className="method-hero-copy">
          <span className="method-eyebrow">Transparent by design</span>
          <h2 id="method-overview-title">A signal for benefit action—not a clinical conclusion</h2>
          <p>
            Activate uses eligibility and claims evidence to identify where a benefit may be relevant. Every
            result remains subject to safety guardrails, data-quality checks, privacy controls, and human
            campaign review.
          </p>
        </div>
        <div className="method-decision" aria-label="Campaign candidate decision rule">
          <span>Eligible member</span>
          <b>+</b>
          <span>Current opportunity</span>
          <b>+</b>
          <span>No suppression</span>
          <b>=</b>
          <strong>Campaign candidate</strong>
        </div>
      </section>

      <div className="method-jump-links" aria-label="Methodology sections">
        {SECTION_LINKS.map(([sectionId, label]) => (
          <Link
            key={sectionId}
            to={`${ACTIVATION_PATHS.methodology}?section=${sectionId}`}
            aria-current={requestedSection === sectionId ? "location" : undefined}
          >
            {label}
          </Link>
        ))}
      </div>

      <section className="method-section" id="identification">
        <div className="method-section-heading">
          <span className="method-section-number">01</span>
          <div>
            <h2>How identification works</h2>
            <p>The same controlled sequence is used across all three benefit domains.</p>
          </div>
        </div>
        <div className="method-process-grid">
          {PROCESS_STEPS.map((step) => (
            <article className="method-process-card" key={step.number}>
              <span>{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
        <div className="method-trust-grid">
          <TrustCard icon="doc" title="Traceable">
            Results retain their evidence period, stage, recommended action, evidence summary, and source
            timing.
          </TrustCard>
          <TrustCard icon="shield" title="Safety-first">
            A convenient or high-scoring route never overrides a stronger guardrail or urgent-care signal.
          </TrustCard>
          <TrustCard icon="lock" title="Privacy-aware">
            Routine HR views use minimum-cohort-protected aggregates rather than member or claim detail.
          </TrustCard>
        </div>
      </section>

      <section className="method-section" id="programs">
        <div className="method-section-heading">
          <span className="method-section-number">02</span>
          <div>
            <h2>Program-specific methods</h2>
            <p>Each domain uses a different clinical grain and precedence model.</p>
          </div>
        </div>
        <div className="method-programs">
          {PROGRAMS.map((program) => (
            <article className="method-program-card" key={program.name}>
              <div className={`method-program-name ${program.name.toLowerCase()}`}>{program.name}</div>
              <dl>
                <div>
                  <dt>Signals considered</dt>
                  <dd>{program.signal}</dd>
                </div>
                <div>
                  <dt>Possible result</dt>
                  <dd>{program.result}</dd>
                </div>
                <div>
                  <dt>Safety precedence</dt>
                  <dd>{program.guardrail}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      </section>

      <section className="method-section" id="guardrails">
        <div className="method-section-heading">
          <span className="method-section-number">03</span>
          <div>
            <h2>Guardrails and governance</h2>
            <p>Identification, outreach eligibility, and campaign approval are separate decisions.</p>
          </div>
        </div>
        <div className="method-guardrail-layout">
          <article className="method-guardrail-card">
            <div className="method-card-title">
              <Icon name="shield" size={20} />
              <h3>Before campaign selection</h3>
            </div>
            <ul>
              <li>Exclude ineligible members and records without a current opportunity.</li>
              <li>Apply urgent, clinical-review, in-person, and program-specific safety precedence.</li>
              <li>Require a blank suppression reason for routine outreach.</li>
              <li>Keep suppressed evidence available for reporting or authorized review.</li>
            </ul>
          </article>
          <article className="method-guardrail-card">
            <div className="method-card-title">
              <Icon name="check" size={20} />
              <h3>Before approval or publication</h3>
            </div>
            <ul>
              <li>Recheck evidence-period quality, enrollment alignment, and minimum cohort size.</li>
              <li>Validate active Workday app templates, message content, and safety language.</li>
              <li>Enforce a different reviewer from the campaign creator.</li>
              <li>Reserve or hold touches using spacing, frequency, same-program, and priority rules.</li>
            </ul>
          </article>
        </div>
        <aside className="method-note">
          <Icon name="info" size={19} strokeWidth={2} />
          <div>
            <strong>Important interpretation</strong>
            <p>
              A guardrail can increase review priority while still blocking routine outreach. Priority is not
              permission to contact.
            </p>
          </div>
        </aside>
      </section>

      <section className="method-section" id="messages">
        <div className="method-section-heading">
          <span className="method-section-number">04</span>
          <div>
            <h2>Message and system limitations</h2>
            <p>Campaign content is intentionally narrower than the underlying evidence.</p>
          </div>
        </div>
        <div className="method-limit-grid">
          <article className="method-limit-card allowed">
            <span className="method-limit-label">
              <Icon name="check" size={15} strokeWidth={2.2} /> Messages may
            </span>
            <ul>
              <li>Explain an available benefit and how to access it.</li>
              <li>Use approved, active copy for the selected program and channel.</li>
              <li>Offer a non-diagnostic next step matched to the campaign goal.</li>
              <li>Include required safety language and channel-appropriate content.</li>
            </ul>
          </article>
          <article className="method-limit-card prohibited">
            <span className="method-limit-label">
              <Icon name="lock" size={15} strokeWidth={2.2} /> Messages must not
            </span>
            <ul>
              <li>State or imply that claims establish a diagnosis or current symptoms.</li>
              <li>Expose diagnoses, claim details, evidence summaries, or suppression reasons.</li>
              <li>Use a sensitive or unsupported channel for the selected cohort.</li>
              <li>Bypass suppression, minimum-cohort, cadence, or reviewer controls.</li>
            </ul>
          </article>
        </div>
        <div className="method-boundary">
          <span className="method-boundary-status">Current boundary</span>
          <div>
            <h3>Publication creates a governed delivery handoff; it does not send a message.</h3>
            <p>
              Approved messages, schedules, and encrypted recipient decisions are frozen at publication.
              Automatic provider delivery, confirmed delivery/read feedback, and enrollment or utilization
              attribution are not connected in the current application.
            </p>
          </div>
        </div>
      </section>

      <section className="method-section method-faq-section" id="faq">
        <div className="method-section-heading">
          <span className="method-section-number">05</span>
          <div>
            <h2>Frequently asked questions</h2>
            <p>Plain-language answers for reviewers, benefit teams, and campaign owners.</p>
          </div>
        </div>
        <div className="method-faq-list">
          {FAQS.map((item, index) => (
            <details key={item.question} open={index === 0}>
              <summary>
                <span>{item.question}</span>
                <Icon name="chev-down" size={17} strokeWidth={2} />
              </summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </Shell>
  );
}
